import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface LogoUploadProps {
  currentLogoUrl?: string | null;
  onUploadSuccess: (url: string) => void;
}

export function LogoUpload({ currentLogoUrl, onUploadSuccess }: LogoUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentLogoUrl || null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an image file',
          variant: 'destructive',
        });
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Please upload an image smaller than 2MB',
          variant: 'destructive',
        });
        return;
      }

      setUploading(true);

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('company-logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('company-logos')
        .getPublicUrl(filePath);

      setPreview(publicUrl);
      onUploadSuccess(publicUrl);

      toast({ title: 'Logo uploaded successfully' });
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploadSuccess('');
  };

  return (
    <div className="space-y-3">
      <Label>Company Logo</Label>
      <input
        type="file"
        id="logo-upload"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        disabled={uploading}
      />
      {preview ? (
        <div className="relative flex h-[120px] w-full items-center justify-center rounded-lg border bg-muted/30 p-3">
          <img
            src={preview}
            alt="Company logo"
            className="max-h-full max-w-full object-contain"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute right-2 top-2 h-6 w-6"
            onClick={handleRemove}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <label
          htmlFor="logo-upload"
          className="flex h-[120px] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
        >
          <ImageIcon className="h-6 w-6" />
          <span className="text-[13px] font-medium">
            {uploading ? 'Uploading...' : 'Click to upload your logo'}
          </span>
          <span className="text-xs">PNG, JPG or WEBP (max 2MB)</span>
        </label>
      )}
      {preview && (
        <label htmlFor="logo-upload">
          <Button type="button" variant="outline" size="sm" disabled={uploading} asChild>
            <span>
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Uploading...' : 'Replace logo'}
            </span>
          </Button>
        </label>
      )}
    </div>
  );
}
